import { useCallback } from 'react'
import { Contract } from '@ethersproject/contracts'
import { MaxUint256 } from '@ethersproject/constants'
import { useAppDispatch } from 'state'
import { updateUserAllowance } from 'state/actions'
import { VaultKey } from 'state/types'
import { useTranslation } from '@pancakeswap/localization'
import { usePoolStakingContract, useSousChef, useVaultPoolContract } from 'hooks/useContract'
import { useToast } from '@pancakeswap/uikit'
import { useCallWithGasPrice } from 'hooks/useCallWithGasPrice'
import useCatchTxError from 'hooks/useCatchTxError'
import { ToastDescriptionWithTx } from 'components/Toast'
import useCakeApprove from 'hooks/useCakeApprove'
import useCakeApprovalStatus from 'hooks/useCakeApprovalStatus'
import { useDeserializedPoolByVaultKey } from 'state/pools/hooks'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { ChainId } from '@pancakeswap/sdk'

export const useApprovePool = (lpContract: Contract, sousId, _earningTokenSymbol) => {
  const { toastSuccess } = useToast()
  const { fetchWithCatchTxError, loading: pendingTx } = useCatchTxError()
  const { callWithGasPrice } = useCallWithGasPrice()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { account, chainId } = useActiveWeb3React()
  const sousChefContract = useSousChef(sousId)

  const handleApprove = useCallback(async () => {
    const receipt = await fetchWithCatchTxError(() => {
      return callWithGasPrice(lpContract, 'approve', [sousChefContract.address, MaxUint256])
    })
    if (receipt?.status) {
      toastSuccess(
        t('Contract Enabled'),
        <ToastDescriptionWithTx txHash={receipt.transactionHash}>
          {t('You can now stake in the pool!')}
        </ToastDescriptionWithTx>,
      )
      dispatch(updateUserAllowance({ sousId, account, chainId }))
    }
  }, [
    account,
    chainId,
    dispatch,
    lpContract,
    sousChefContract,
    sousId,
    t,
    toastSuccess,
    callWithGasPrice,
    fetchWithCatchTxError,
  ])

  return { handleApprove, pendingTx }
}

// Approve BBC auto pool
export const useVaultApprove = (vaultKey: VaultKey, setLastUpdated: () => void) => {
  const {chainId} = useActiveWeb3React()
  const vaultPoolContract = useVaultPoolContract(vaultKey, chainId)
  const cakeContracts = usePoolStakingContract(vaultKey, chainId)
  const pool = useDeserializedPoolByVaultKey(vaultKey)
  const { t } = useTranslation()
  return useCakeApprove(
    setLastUpdated,
    vaultPoolContract?.address,
    cakeContracts,
    t('You can now stake in the %symbol% vault!', { symbol: pool?.stakingToken.symbol }),
  )
}

export const useCheckVaultApprovalStatus = (vaultKey: VaultKey, chainId: ChainId) => {
  const vaultPoolContract = useVaultPoolContract(vaultKey, chainId)
  const cakeContracts = usePoolStakingContract(vaultKey, chainId)
  return useCakeApprovalStatus(vaultPoolContract?.address, cakeContracts)
}
